import { Component, computed, effect, inject } from '@angular/core';
import { CartItemStateService } from '../../../services/cart-item-state.service';
import { LibModulesWolcModule } from '../../lib-modules-wolc.module';
import { ScheduleItemDto } from '../../../model/product';

interface TimeOption {
  value: string;
  label: string;
  minutes: number;
}

@Component({
  selector: 'btlib-order-timegrid',
  standalone: true,
  imports: [LibModulesWolcModule],
  templateUrl: './order-timegrid.component.html',
  styleUrl: './order-timegrid.component.scss'
})
export class BtOrderTimegridComponent {
  state = inject(CartItemStateService);

  product = this.state.product;
  period = this.state.period;
  timeItems = this.state.timeItems;

  useTimegrid = computed(() => !!this.product()?.useTimegrid);
  minDurationMinutes = computed(() => this.product()?.timegridDurationMinutes ?? this.inferSlotDuration());

  startOptions = computed(() => this.buildStartOptions(this.timeItems()));
  selectedStart = computed(() => this.period().startTime || null);

  endOptions = computed(() => this.buildEndOptions(this.timeItems(), this.minutesFromTime(this.selectedStart()), this.minDurationMinutes()));
  selectedEnd = computed(() => this.period().endTime || null);

  constructor() {
    effect(() => this.syncSelection());
  }

  async onStartChange(value: string | null): Promise<void> {
    const startMinutes = value != null ? this.minutesFromTime(value) : null;
    if (startMinutes == null) return;
    const endMinutes = this.findDefaultEndMinutes(startMinutes);
    if (endMinutes != null) {
      await this.state.updateTime(this.minutesToTime(startMinutes), this.minutesToTime(endMinutes));
    }
  }

  async onEndChange(value: string | null): Promise<void> {
    const start = this.selectedStart();
    if (!start || value == null) return;
    await this.state.updateTime(start, value);
  }

  private syncSelection(): void {
    const start = this.selectedStart();
    const end = this.selectedEnd();
    if (!start) return;
    const startMinutes = this.minutesFromTime(start);
    if (startMinutes == null) return;
    const minDuration = this.minDurationMinutes();

    if (end == null) {
      const defaultEnd = this.findDefaultEndMinutes(startMinutes);
      if (defaultEnd != null) {
        this.state.updateTime(start, this.minutesToTime(defaultEnd));
      }
      return;
    }

    const endMinutes = this.minutesFromTime(end);
    if (endMinutes == null) return;
    if (endMinutes - startMinutes < minDuration) {
      const adjustedEnd = this.findDefaultEndMinutes(startMinutes);
      if (adjustedEnd != null) {
        this.state.updateTime(start, this.minutesToTime(adjustedEnd));
      }
    }
  }

  private findDefaultEndMinutes(startMinutes: number): number | null {
    const options = this.buildEndOptions(this.timeItems(), startMinutes, this.minDurationMinutes());
    return options.length > 0 ? options[0].minutes : null;
  }

  private buildStartOptions(items: ScheduleItemDto[]): TimeOption[] {
    const map = new Map<number, ScheduleItemDto>();
    for (const item of items) {
      if (item.available <= 0 || item.startTime == null) continue;
      if (!map.has(item.startTime)) {
        map.set(item.startTime, item);
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([minutes]) => ({ value: this.minutesToTime(minutes), label: this.minutesToTime(minutes), minutes }));
  }

  private buildEndOptions(items: ScheduleItemDto[], startMinutes: number | null, minDuration: number): TimeOption[] {
    if (startMinutes == null) return [];
    const sorted = [...items].sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
    const startIdx = sorted.findIndex(item => item.startTime === startMinutes && item.available > 0);
    if (startIdx < 0) return [];

    const options: TimeOption[] = [];
    let prevEnd = startMinutes;
    for (let i = startIdx; i < sorted.length; i++) {
      const item = sorted[i];
      if (item.available <= 0 || item.startTime == null || item.endTime == null) break;
      if (item.startTime !== prevEnd) break;
      const duration = item.endTime - startMinutes;
      if (duration >= minDuration) {
        options.push({ value: this.minutesToTime(item.endTime), label: this.minutesToTime(item.endTime), minutes: item.endTime });
      }
      prevEnd = item.endTime;
    }
    return options;
  }

  private inferSlotDuration(): number {
    const items = this.timeItems();
    if (items.length >= 2 && items[0].startTime != null && items[1].startTime != null) {
      return items[1].startTime - items[0].startTime;
    }
    return 30;
  }

  private minutesToTime(minutes: number | null | undefined): string {
    if (minutes == null) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  private minutesFromTime(time: string | null): number | null {
    if (!time) return null;
    const [h, m] = time.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }
}
